/**
 * Navigation Utilities for Web
 * Helpers for opening maps and navigation
 */

export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Extract coordinates from location object
 * Handles both GeoJSON format and lat/lng format
 */
export const getLocationCoordinates = (location: {
  geo?: { type?: string; coordinates?: [number, number]; lat?: number; lng?: number };
  coordinates?: { lat: number; lng: number };
}): Coordinates | null => {
  // Check GeoJSON format first (coordinates are [longitude, latitude])
  if (location.geo?.coordinates && location.geo.coordinates.length === 2) {
    const [lng, lat] = location.geo.coordinates;
    if (lat !== 0 || lng !== 0) {
      return { latitude: lat, longitude: lng };
    }
  }
  
  // Check geo.lat/lng format (from API transformation)
  if (location.geo && 'lat' in location.geo && 'lng' in location.geo) {
    const { lat, lng } = location.geo as { lat: number; lng: number };
    if (lat !== 0 || lng !== 0) {
      return { latitude: lat, longitude: lng };
    }
  }
  
  // Check simple coordinates format
  if (location.coordinates?.lat && location.coordinates?.lng) {
    return {
      latitude: location.coordinates.lat,
      longitude: location.coordinates.lng,
    };
  }
  
  return null;
};

/**
 * Open location in Google Maps for navigation
 */
export const openDirections = (
  destination: Coordinates,
  destinationName?: string
): void => {
  const { latitude, longitude } = destination;
  const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  window.open(url, '_blank');
};

/**
 * Open location in Google Maps (view only)
 */
export const openInMaps = (
  location: Coordinates,
  locationName?: string
): void => {
  const { latitude, longitude } = location;
  const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  window.open(url, '_blank');
};

/**
 * Format distance for display
 */
export const formatDistance = (distanceKm: number): string => {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};
